import { useMemo, useState } from 'react';
import { formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CivilRecord, CivilRecordVersion, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const REGISTRAR_ROLES = ['REGISTRAR', 'DEPUTY_REGISTRAR'];

/** Civil Registry — search the M01 Population Register and drill into a record's version history. */
export function CivilRecords() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (appliedQ) params.set('q', appliedQ);
    if (status) params.set('status', status);
    return `/api/v1/m01/civil-records?${params.toString()}`;
  }, [appliedQ, status]);

  const listQ = useApiQuery<Page<CivilRecord>>(path);

  return (
    <div>
      <PageHeader
        title="Civil Records"
        subtitle="Search the Population Register — birth/death civil status records (M01)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input
              className="field-input !w-56"
              placeholder="Search NIN or name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="ALIVE">Alive</option>
              <option value="DECEASED">Deceased</option>
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>
        }
      />

      {!isRegistrar ? <RoleNotice roles={REGISTRAR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No civil records match" hint="Try a different NIN, name, or status." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Name</th>
                <th className="table-head">DOB</th>
                <th className="table-head">Sex</th>
                <th className="table-head">District</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(r.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{r.nin}</td>
                  <td className="table-cell">{r.fullName}</td>
                  <td className="table-cell">{formatDate(r.dob)}</td>
                  <td className="table-cell">{r.sex}</td>
                  <td className="table-cell text-xs text-slate-500">{r.districtOfResidence}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? <RecordDetailModal id={selectedId} onClose={() => setSelectedId(null)} /> : null}
    </div>
  );
}

function RecordDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const recordQ = useApiQuery<CivilRecord>(`/api/v1/m01/civil-records/${id}`);
  const historyQ = useApiQuery<{ items: CivilRecordVersion[] }>(`/api/v1/m01/civil-records/${id}/history`);
  const record = recordQ.data;

  return (
    <Modal title={record ? record.fullName : 'Civil record'} onClose={onClose} wide>
      {recordQ.error ? <ErrorNote message={recordQ.error} /> : null}
      {recordQ.loading ? <Spinner /> : null}
      {record ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="NIN" value={record.nin} mono />
            <Detail label="Status" value={<StatusBadge status={record.status} />} />
            <Detail label="Sex" value={record.sex} />
            <Detail label="Date of birth" value={formatDate(record.dob)} />
            <Detail label="Place of birth" value={record.placeOfBirth} />
            <Detail label="District of residence" value={record.districtOfResidence} />
            <Detail label="Nationality" value={record.nationality.join(', ') || '—'} />
            <Detail label="Current version" value={String(record.currentVersion)} />
            {record.dateOfDeath ? <Detail label="Date of death" value={formatDate(record.dateOfDeath)} /> : null}
          </div>

          <div>
            <div className="field-label">Parents on file</div>
            {record.parents.length === 0 ? (
              <p className="text-sm text-slate-400">None on file.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {record.parents.map((p, i) => (
                  <li key={i} className="text-slate-700">
                    <span className="font-medium">{p.relation}</span>: {p.name} {p.nin ? <span className="font-mono text-xs text-slate-400">({p.nin})</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className="field-label">Version history (registry-kit)</div>
            {historyQ.error ? <ErrorNote message={historyQ.error} /> : null}
            {historyQ.loading ? (
              <Spinner />
            ) : (historyQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-400">No recorded versions.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="table-head">Version</th>
                      <th className="table-head">Effective from</th>
                      <th className="table-head">Reason</th>
                      <th className="table-head">Authorised by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyQ.data!.items.map((v) => (
                      <tr key={v.id}>
                        <td className="table-cell font-medium">v{v.version}</td>
                        <td className="table-cell">{formatDate(v.effectiveFrom)}</td>
                        <td className="table-cell text-xs text-slate-500">{v.changeReason ?? '—'}</td>
                        <td className="table-cell text-xs text-slate-500">{v.authorisedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
